import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { canEdit } from "../../lib/permissions";
import { RxEditor } from "./RxEditor";
import { RxDetail } from "./RxDetail";
import { RxSignDialog } from "./RxSignDialog";

const clientOf = (rx) => ({ id: rx.client_id, first_name: rx.client_name || "", last_name: "" });

/** Shared prescription panels (editor, detail, sign) for the chart tab, the Master List, and the Prescriber Dashboard. */
export function useRxPanels(onChanged) {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [pharmacies, setPharmacies] = useState([]);
  const [editor, setEditor] = useState(null);
  const [detail, setDetail] = useState(null);
  const [signing, setSigning] = useState(null);
  const cb = useRef(onChanged);
  cb.current = onChanged;
  useEffect(() => {
    api.get("/prescriptions/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/pharmacy-directory").then((r) => setPharmacies(r.data)).catch(() => {});
  }, []);
  const me = meta ? { id: user.id, role: user.role, can_edit: canEdit(user, "clinical"), ...meta.me } : null;
  const openRx = useCallback((id) => api.get(`/prescriptions/${id}`).then((r) => setDetail(r.data)).catch((e) => toast.error(apiError(e))), []);
  const newRx = (client) => setEditor({ client, rx: null });
  const editRx = (rx) => { cb.current?.(); setDetail(rx); setEditor({ client: clientOf(rx), rx }); };
  const changed = (rx) => { setDetail(rx); cb.current?.(); };

  const panels = meta && (
    <>
      {editor && <RxEditor open client={editor.client} rx={editor.rx} meta={meta} me={me} onClose={() => setEditor(null)}
        onSaved={(rx, thenSign) => { setEditor(null); changed(rx); if (thenSign) setSigning(rx); }} />}
      <RxDetail rx={detail} open={!!detail && !editor} meta={meta} me={me} pharmacies={pharmacies} onClose={() => setDetail(null)}
        onChange={(rx) => (rx ? changed(rx) : (setDetail(null), cb.current?.()))}
        onEdit={() => setEditor({ client: clientOf(detail), rx: detail })} onSign={() => setSigning(detail)}
        onRenewed={editRx} onOpen={openRx} />
      {signing && <RxSignDialog rx={signing} open onClose={() => setSigning(null)} onSigned={(rx) => { setSigning(null); changed(rx); }} />}
    </>
  );
  return { meta, me, openRx, newRx, editRx, pharmacies, panels };
}
